import { useEffect, useMemo, useRef } from 'react';
import type { UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { type IntakeResponse } from '../../services/api';
import type { KinnserClinicDataResponse } from '../../services/api/types.gen';
import type { IntakeRecord } from '../../store/slices/intakeSlice';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { createNameMatcher } from '../../utils/fuzzyMatch';
import { filterAgencyInsurancesByClinic } from '../../utils/insuranceOptions';
import type { CreatePatientFormData } from './types';
import { resolveClinicKeyFromAgencyClinicId } from '../../utils/clinicKeyUtils';
import { getAgencyClinicDisplayName } from '../../utils/agencyClinicDisplayName';
import { useKinnserClinicDataShared } from './useKinnserClinicDataShared';
import type { ClinicSelectClinic } from '../../components/clinic/ClinicSelect';

interface UseClinicDataOptions {
  agencySystemName: string | undefined;
  /** The agency_clinic_id (UUID) selected in the form. */
  agencyClinicId: string | null | undefined;
  /** Agency offers Kinnser intake; required to resolve WellSky clinic_key and fetch catalog. */
  hasKinnserOption: boolean;
  /** Live form value; combined with intake default to pick authoritative clinic source. */
  pushToKinnser: boolean | null | undefined;
  /** Live form value; combined with intake default to pick authoritative clinic source. */
  pushToRoger: boolean | null | undefined;
  selectedIntake: IntakeRecord | null;
  setValue: UseFormSetValue<CreatePatientFormData>;
  trigger: UseFormTrigger<CreatePatientFormData>;
  intakeId: string | undefined;
  saveToServer: (updates: Partial<CreatePatientFormData>) => Promise<void>;
}

interface UseClinicDataReturn {
  clinics: ClinicSelectClinic[];
  clinicData: KinnserClinicDataResponse | null;
  loadingClinics: boolean;
  loadingClinicData: boolean;
  insuranceOptions: { name: string; id: string }[];
}

type AgencyClinicRow = ReturnType<
  typeof useStore.getState
>['agencyClinics'][number];

/** Kinnser-only: resolve WellSky clinic_key and load catalog from RogerDoc via `useKinnserClinicDataShared`. */
function useKinnserClinicCatalogFetch({
  agencySystemName,
  agencyClinics,
  agencyClinicId,
  kinnserCatalogEnabled,
}: {
  agencySystemName: string | undefined;
  agencyClinics: AgencyClinicRow[];
  agencyClinicId: string | null | undefined;
  /** True when push target is Kinnser and the agency supports Kinnser. */
  kinnserCatalogEnabled: boolean;
}) {
  const resolvedClinicKey = useMemo(() => {
    if (!kinnserCatalogEnabled) return null;
    return resolveClinicKeyFromAgencyClinicId(agencyClinics, agencyClinicId);
  }, [kinnserCatalogEnabled, agencyClinics, agencyClinicId]);

  const { clinicData, loadingClinicData } = useKinnserClinicDataShared({
    agencySystemName,
    kinnserClinicId: resolvedClinicKey,
    enabled: kinnserCatalogEnabled,
  });

  return {
    kinnserClinicData: clinicData,
    loadingKinnserClinicData: loadingClinicData,
  };
}

/** Roger EMR: minimal clinic shape from `agency_clinics` when the intake push target is Roger. */
function useRogerEmrClinicSnapshot({
  agencyClinicId,
  selectedAgencyClinic,
  effectivePushToRoger,
}: {
  agencyClinicId: string | null | undefined;
  selectedAgencyClinic: AgencyClinicRow | undefined;
  effectivePushToRoger: boolean;
}) {
  const rogerEmrClinicDataFromAgency =
    useMemo((): KinnserClinicDataResponse | null => {
      if (!agencyClinicId || !selectedAgencyClinic) return null;
      if (!effectivePushToRoger) return null;
      return {
        auto_mrn: Boolean(selectedAgencyClinic.auto_mrn),
        clinic_key: selectedAgencyClinic.clinic_key?.trim() ?? '',
        clinic_name: selectedAgencyClinic.name,
      };
    }, [agencyClinicId, selectedAgencyClinic, effectivePushToRoger]);

  return { rogerEmrClinicDataFromAgency };
}

/**
 * Hook for fetching and managing clinic data.
 *
 * The clinic dropdown always uses agency_clinic_id as the option value so
 * clinics without a Kinnser clinic_key still display correctly. When the
 * intake push target is Kinnser, the clinic_key is resolved from agencyClinics
 * and passed to RogerDoc for WellSky catalog (physicians, branches, etc.).
 * When the push target is Roger EMR, MRN and related defaults come from `agency_clinics`.
 */
export function useClinicData({
  agencySystemName,
  agencyClinicId,
  hasKinnserOption,
  pushToKinnser,
  pushToRoger,
  selectedIntake,
  setValue,
  trigger,
  intakeId,
  saveToServer,
}: UseClinicDataOptions): UseClinicDataReturn {
  const agencyClinics = useStore(state => state.agencyClinics);
  const agencyClinicsLoading = useStore(state => state.agencyClinicsLoading);
  const fetchAgencyClinics = useStore(state => state.fetchAgencyClinics);

  const effectivePushToKinnser =
    pushToKinnser === true || selectedIntake?.push_to_kinnser === true;
  const effectivePushToRoger =
    pushToRoger === true || selectedIntake?.push_to_roger === true;

  const kinnserCatalogEnabled = hasKinnserOption && effectivePushToKinnser;

  const { agencyInsurances, agencyInsurancesLoading, fetchAgencyInsurances } =
    useStore(
      useShallow(state => ({
        agencyInsurances: state.agencyInsurances,
        agencyInsurancesLoading: state.agencyInsurancesLoading,
        fetchAgencyInsurances: state.fetchAgencyInsurances,
      }))
    );

  useEffect(() => {
    if (agencyClinics.length === 0 && !agencyClinicsLoading) {
      fetchAgencyClinics();
    }
  }, [agencyClinics.length, agencyClinicsLoading, fetchAgencyClinics]);

  const selectedAgencyClinic = useMemo(
    () => agencyClinics.find(c => c.id === agencyClinicId),
    [agencyClinics, agencyClinicId]
  );

  const { kinnserClinicData, loadingKinnserClinicData } =
    useKinnserClinicCatalogFetch({
      agencySystemName,
      agencyClinics,
      agencyClinicId,
      kinnserCatalogEnabled,
    });

  const { rogerEmrClinicDataFromAgency } = useRogerEmrClinicSnapshot({
    agencyClinicId,
    selectedAgencyClinic,
    effectivePushToRoger,
  });

  // Always use agencyClinics for the dropdown, keyed by agency_clinic_id
  const clinicOptions = useMemo(
    () =>
      agencyClinics.map(ac => ({
        id: ac.id,
        name: getAgencyClinicDisplayName(ac, {
          totalClinicCount: agencyClinics.length,
        }),
        clinic_source: ac.clinic_source,
        clinic_key: ac.clinic_key,
        branches: ac.branches,
        city: ac.city,
        state: ac.state,
      })),
    [agencyClinics]
  );

  // Mutual exclusivity is enforced in the UI; if both flags were true, Kinnser catalog wins.
  const clinicData = useMemo(() => {
    if (effectivePushToKinnser) return kinnserClinicData;
    if (effectivePushToRoger) return rogerEmrClinicDataFromAgency;
    return null;
  }, [
    effectivePushToKinnser,
    effectivePushToRoger,
    kinnserClinicData,
    rogerEmrClinicDataFromAgency,
  ]);

  const loadingClinicData = effectivePushToKinnser
    ? loadingKinnserClinicData
    : false;

  // Ensure agency insurances are loaded (needed when clinic has no clinic_key)
  useEffect(() => {
    if (agencyInsurances.length === 0 && !agencyInsurancesLoading) {
      fetchAgencyInsurances();
    }
  }, [agencyInsurances.length, agencyInsurancesLoading, fetchAgencyInsurances]);

  // Build insurance options: prefer Kinnser clinic data, fall back to agency_insurances
  const insuranceOptions = useMemo(() => {
    if (
      clinicData?.insurance_providers &&
      clinicData.insurance_providers.length > 0
    ) {
      return clinicData.insurance_providers;
    }
    if (!agencyClinicId) return [];
    const filtered = filterAgencyInsurancesByClinic(
      agencyInsurances,
      agencyClinicId
    );
    return filtered.map(ai => ({ id: ai.id, name: ai.name }));
  }, [clinicData?.insurance_providers, agencyClinicId, agencyInsurances]);

  // Track previous clinic ID to detect user-initiated changes vs initial load
  const previousClinicIdRef = useRef<string | null | undefined>(undefined);

  // Track if auto-select has been done for this intake
  const autoSelectDoneRef = useRef(false);

  // Reset auto-select flag when intakeId changes
  useEffect(() => {
    autoSelectDoneRef.current = false;
  }, [intakeId]);

  // Auto-select clinic if there's only one and not already set
  useEffect(() => {
    if (autoSelectDoneRef.current) return;
    if (agencyClinicsLoading || clinicOptions.length !== 1) return;

    const intakeData = selectedIntake as unknown as IntakeResponse;
    if (!intakeData?.agency_clinic_id) {
      autoSelectDoneRef.current = true;
      const clinic = agencyClinics[0];
      const clinicId = clinic.id;
      setValue('agency_clinic_id', clinicId);
      saveToServer({
        agency_clinic_id: clinicId,
      } as Partial<CreatePatientFormData>).catch(err => {
        console.error('Failed to auto-save clinic selection:', err);
      });
    }
  }, [
    clinicOptions,
    agencyClinics,
    agencyClinicsLoading,
    selectedIntake,
    setValue,
    saveToServer,
    intakeId,
  ]);

  // Trigger validation after clinic data is loaded
  useEffect(() => {
    if (clinicData && !loadingClinicData) {
      setTimeout(() => trigger(), 0);
    }
  }, [clinicData, loadingClinicData, trigger]);

  // Handle fuzzy matching when clinic data or intake data changes
  useEffect(() => {
    if (!clinicData || !selectedIntake) return;

    const intakeData = selectedIntake as unknown as IntakeResponse;

    const autoMatchedValues: Partial<CreatePatientFormData> = {};

    if (clinicData.branches?.length === 1 && !intakeData?.kinnser_branch_id) {
      setValue('kinnser_branch_id', clinicData.branches[0].id);
      autoMatchedValues.kinnser_branch_id = clinicData.branches[0].id;
    }

    const referringPhysician = selectedIntake?.referring_physician;

    if (
      referringPhysician &&
      clinicData.physicians?.length &&
      !selectedIntake?.kinnser_physician_id
    ) {
      const matcher = createNameMatcher(clinicData.physicians);
      const match = matcher.findBestMatch(referringPhysician);

      if (match) {
        console.log(
          `Auto-matching physician "${referringPhysician}" to "${match.name}"`
        );
        setValue('kinnser_physician_id', match.id);
        autoMatchedValues.kinnser_physician_id = match.id;
      }
    }

    if (Object.keys(autoMatchedValues).length > 0) {
      saveToServer(autoMatchedValues);
    }
  }, [clinicData, selectedIntake, saveToServer, setValue]);

  useEffect(() => {
    if (agencyClinicId && previousClinicIdRef.current === undefined) {
      previousClinicIdRef.current = agencyClinicId;
    } else if (
      agencyClinicId &&
      previousClinicIdRef.current !== agencyClinicId
    ) {
      previousClinicIdRef.current = agencyClinicId;
    }
  }, [agencyClinicId]);

  return {
    clinics: clinicOptions,
    clinicData,
    loadingClinics: agencyClinicsLoading,
    loadingClinicData,
    insuranceOptions,
  };
}
